import { z } from 'zod';
import { CodeEvidenceSchema } from './diagram.js';
import { HttpMethod } from './apiRoutes.js';
import { ResponseHandlingSchema } from './userInteraction.js';
// ============= API Call Tracer Types =============
/**
 * Issue #435: Typed schema field - represents a field with its type information
 */
export const TypedSchemaFieldSchema = z.lazy(() => z.object({
    /** Field name */
    name: z.string(),
    /** Type (e.g., "string", "number", "boolean", "array", "object") */
    type: z.string(),
    /** Whether the field is optional */
    isOptional: z.boolean().optional(),
    /** Whether the field can be null */
    isNullable: z.boolean().optional(),
    /** Nested fields for object types */
    nestedFields: z.array(TypedSchemaFieldSchema).optional(),
    /** Element type for array types */
    arrayType: z.string().optional(),
    /** Possible values for enum types */
    enumValues: z.array(z.string()).optional(),
}));
/**
 * API client library types
 * Issue #480: Added tRPC and GraphQL support
 * Issue #484: Added SDK, MobX, and Server Actions support
 */
export const ApiClientLibrary = z.enum([
    'fetch',
    'axios',
    'ky',
    'got',
    'superagent',
    'node-fetch',
    'undici',
    'trpc',
    'graphql',
    'sdk',
    'mobx',
    'server-action',
    'xhr',
    'http',
    'websocket',
    'sse',
    'orpc',
    // Issue #793: Supabase JS client (supabase.from/.rpc/.storage)
    'supabase-js',
    'unknown',
]);
/**
 * Request body schema type
 */
export const RequestBodyType = z.enum([
    'json',
    'form-data',
    'form-urlencoded',
    'text',
    'binary',
    'unknown',
]);
// ============= API Call Schema =============
/**
 * Represents a detected API call in frontend/backend code
 */
export const ApiCallSchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** HTTP method */
    method: HttpMethod,
    /** API endpoint (may be dynamic) */
    endpoint: z.string(),
    /** Whether endpoint contains dynamic segments */
    isDynamicEndpoint: z.boolean(),
    /** Base URL if configured */
    baseUrl: z.string().optional(),
    /** Full URL if resolvable */
    fullUrl: z.string().optional(),
    /** API client library used */
    library: ApiClientLibrary,
    /** Request body type */
    bodyType: RequestBodyType.optional(),
    /** Request headers detected */
    headers: z.record(z.string()).optional(),
    /** Query parameters detected */
    queryParams: z.array(z.string()).optional(),
    /** Path parameters detected */
    pathParams: z.array(z.string()).optional(),
    /** Wrapper function name (e.g., useQuery key, axios instance method) */
    wrapperFunction: z.string().optional(),
    /** Whether this call is authenticated */
    isAuthenticated: z.boolean().optional(),
    /** Response type hint */
    responseType: z.string().optional(),
    /** File where call is made */
    sourceFile: z.string(),
    /** Line number */
    sourceLine: z.number(),
    /** Component/function containing the call */
    sourceContext: z.string().optional(),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
    /** Response handling information (#369) */
    responseHandling: ResponseHandlingSchema.optional(),
    /** Detection confidence (#699) */
    confidence: z.enum(['deterministic', 'heuristic']).optional(),
    /** Reason for confidence level (#699) */
    confidenceReason: z.string().optional(),
});
// ============= API Route Schema =============
/**
 * Represents a server-side API route definition
 */
export const ApiRouteSchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** HTTP method(s) supported */
    methods: z.array(HttpMethod),
    /** Route path pattern */
    path: z.string(),
    /** Path with parameter placeholders resolved */
    resolvedPath: z.string().optional(),
    /** Path parameters */
    pathParams: z.array(z.object({
        name: z.string(),
        type: z.string().optional(),
        required: z.boolean(),
    })),
    /** Query parameter schemas */
    queryParams: z.array(z.object({
        name: z.string(),
        type: z.string().optional(),
        required: z.boolean(),
    })).optional(),
    /** Request body schema if detected */
    requestBodySchema: z.unknown().optional(),
    /** Response schema if detected */
    responseSchema: z.unknown().optional(),
    /** Request body field names (Issue #390) */
    requestBodyFields: z.array(z.string()).optional(),
    /** Request body schema name if using Zod/Yup (Issue #390) */
    requestBodySchemaName: z.string().optional(),
    /** Request body typed fields (Issue #435) */
    requestBodyTypedFields: z.array(TypedSchemaFieldSchema).optional(),
    /** Response field names (Issue #390) — back-compat: union across statuses */
    responseFields: z.array(z.string()).optional(),
    /** Response schema name if typed (Issue #390) */
    responseSchemaName: z.string().optional(),
    /** Response typed fields (Issue #435) */
    responseTypedFields: z.array(TypedSchemaFieldSchema).optional(),
    /**
     * Issue #898 F4: per-status response shapes.
     * Keys are HTTP status codes; values describe each branch's body.
     */
    responses: z.record(z.coerce.number(), z.object({
        fields: z.array(z.string()).optional(),
        bodyType: z.enum(['object', 'empty', 'scalar', 'array', 'unknown']).optional(),
        /**
         * #1339 item 9 — server-side redirect target. When the handler
         * calls `res.redirect('/path')` / `reply.redirect('/path')` /
         * `Response.redirect('/path')`, the static lifter (#1339 item 9)
         * populates this for `responses['302']`. The legacy-shape adapter
         * emits a downstream `navigation` step pointing at this target.
         */
        redirectTo: z.string().optional(),
    })).optional(),
    /** Handler function name */
    handlerFunction: z.string(),
    /** Middleware applied */
    middleware: z.array(z.string()).optional(),
    /** Framework used */
    framework: z.enum(['express', 'fastify', 'koa', 'hapi', 'nest', 'next', 'remix', 'sveltekit', 'nuxt', 'hono', 'elysia', 'solid-start', 'supabase-postgrest', 'trpc', 'axum', 'actix', 'rocket', 'warp', 'spring', 'fastapi', 'unknown']),
    /** File where route is defined */
    sourceFile: z.string(),
    /** Line number */
    sourceLine: z.number(),
    /** End line number of handler function (for scoping database queries) */
    sourceEndLine: z.number().optional(),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
    /** Detection confidence (#699) */
    confidence: z.enum(['deterministic', 'heuristic']).optional(),
    /** Reason for confidence level (#699) */
    confidenceReason: z.string().optional(),
    /**
     * veodiagram-issues#82 — when this route was synthesized from a
     * multiplexer dispatcher (e.g. `POST /api/jade?r=createComputer`
     * is a child of `POST /api/jade`), `parentRouteId` is the parent's `id`.
     * Undefined for normal first-class routes.
     */
    parentRouteId: z.string().optional(),
    /**
     * veodiagram-issues#82 — when this route IS a multiplexer wrapper that
     * has been expanded, `multiplexerChildren` lists the child route ids.
     * Consumers use this to suppress double-counting parent's handler edges
     * when child handlers exist.
     */
    multiplexerChildren: z.array(z.string()).optional(),
});
// ============= API Call Mapping =============
/**
 * Represents a matched pair of API call -> API route
 */
export const ApiCallMappingSchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** The detected API call */
    call: ApiCallSchema,
    /** The matched API route (if found) */
    route: ApiRouteSchema.optional(),
    /** Confidence of the match (0-1) */
    matchConfidence: z.number(),
    /** How the match was determined */
    matchMethod: z.enum(['exact', 'pattern', 'inferred', 'manual']),
    /** Whether this is a cross-service call */
    isCrossService: z.boolean(),
    /** Target service if cross-service */
    targetService: z.string().optional(),
});
// ============= API Endpoint Summary =============
/**
 * Summary of an API endpoint with all calls and routes
 */
export const ApiEndpointSummarySchema = z.object({
    /** Normalized endpoint path */
    endpoint: z.string(),
    /** HTTP methods used */
    methods: z.array(HttpMethod),
    /** All calls to this endpoint */
    calls: z.array(z.object({
        id: z.string(),
        sourceFile: z.string(),
        sourceLine: z.number(),
        method: HttpMethod,
    })),
    /** Route definitions for this endpoint */
    routes: z.array(z.object({
        id: z.string(),
        sourceFile: z.string(),
        sourceLine: z.number(),
        handlerFunction: z.string(),
    })),
    /** Whether this endpoint has both calls and routes */
    isFullyMapped: z.boolean(),
});
// ============= API Call Tracer Result =============
/**
 * Complete result from API call tracing
 */
export const ApiCallTracerResultSchema = z.object({
    /** All detected API calls */
    calls: z.array(ApiCallSchema),
    /** All detected API routes */
    routes: z.array(ApiRouteSchema),
    /** Matched call-to-route mappings */
    mappings: z.array(ApiCallMappingSchema),
    /** Summary by endpoint */
    endpointSummaries: z.array(ApiEndpointSummarySchema),
    /** Summary statistics */
    summary: z.object({
        totalCalls: z.number(),
        totalRoutes: z.number(),
        mappedCalls: z.number(),
        unmappedCalls: z.number(),
        unmappedRoutes: z.number(),
        crossServiceCalls: z.number(),
        byMethod: z.record(z.number()),
        byLibrary: z.record(z.number()),
    }),
});
// ============= API Service Definition Types (Issue #335) =============
/**
 * Represents a method in an API service wrapper (e.g., api.startAnalysis)
 */
export const ApiServiceMethodSchema = z.object({
    /** Method name (e.g., "startAnalysis") */
    name: z.string(),
    /** HTTP method */
    httpMethod: HttpMethod,
    /** Endpoint pattern (e.g., "/projects/${projectId}/analyze") */
    endpoint: z.string(),
    /** Whether endpoint is dynamic */
    isDynamicEndpoint: z.boolean(),
    /** Path parameters extracted from endpoint */
    pathParams: z.array(z.string()),
    /** Source file where method is defined */
    sourceFile: z.string(),
    /** Line number of method definition */
    sourceLine: z.number(),
    /** End line of method definition */
    sourceEndLine: z.number(),
    /** The fetch/axios wrapper function used (e.g., "fetchApi") */
    wrapperFunction: z.string().optional(),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
});
/**
 * Represents an API service definition (e.g., export const api = { ... })
 */
export const ApiServiceDefinitionSchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** Export name (e.g., "api") */
    exportName: z.string(),
    /** Import path pattern (e.g., "../services/api" or "@/services/api") */
    importPath: z.string(),
    /** All methods defined in the service */
    methods: z.array(ApiServiceMethodSchema),
    /** Source file path */
    sourceFile: z.string(),
    /** Whether this is a default export */
    isDefaultExport: z.boolean(),
    /** Internal fetch wrapper function name if detected */
    fetchWrapper: z.string().optional(),
});
/**
 * Tracks an import of an API service in a component file
 */
export const ApiServiceImportSchema = z.object({
    /** Local variable name (e.g., "api") */
    localName: z.string(),
    /** Import specifier (e.g., "api" from named import, or default) */
    importedName: z.string(),
    /** Import path (e.g., "../services/api") */
    importPath: z.string(),
    /** Whether this is a default import */
    isDefaultImport: z.boolean(),
    /** File where import appears */
    sourceFile: z.string(),
    /** Line number of import */
    sourceLine: z.number(),
});
//# sourceMappingURL=apiCallTracer.js.map